import { zodResolver } from '@hookform/resolvers/zod';
import { StaticModal, createModal } from '@nexelem/modals';
import { Button } from '@nexelem/components';
import { FormProvider, useForm } from 'react-hook-form';
import {
  NAME_LENGTH,
  QueueDetailsFormSchema,
  SHORT_NAME_LENGTH,
  queueDetailsFormSchema,
} from './queueDetailsFormSchema';
import { Queue } from '@nexelem/graphne-api';
import { useCustomTranslation } from '../../../../../hooks';
import { useProductionQueuePropertiesMutation } from '../../../../../queryHooks/useProductionQueuePropertiesMutation';
import { FormTextInput } from '@nexelem/form-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIndustryWindows } from '@fortawesome/pro-light-svg-icons';

type QueueDetailsModalProps = {
  queue: Queue;
};

const QueueDetailsModal = ({ queue }: QueueDetailsModalProps) => {
  const queueMutation = useProductionQueuePropertiesMutation();
  const { tWrapper } = useCustomTranslation('products.productionPlan.settingDrawer.queueDetailsModal');

  const { handleSubmit, ...methods } = useForm<QueueDetailsFormSchema>({
    mode: 'onSubmit',
    resolver: zodResolver(queueDetailsFormSchema),
    defaultValues: {
      name: queue.name,
      shortName: queue.shortName,
    },
  });

  const onSubmit = (formData: QueueDetailsFormSchema) => {
    if (!queue.id) {
      return;
    }

    queueMutation.mutate(
      {
        queueId: queue.id.toString(),
        queueProperties: formData,
      },
      {
        onSuccess: () => {
          closeQueueDetailsModal();
        },
      },
    );
  };

  const externalName = (queue.externalId as { name?: string })?.name;

  return (
    <StaticModal component="form" onClose={closeQueueDetailsModal} onSubmit={handleSubmit(onSubmit)}>
      <FormProvider {...methods} handleSubmit={handleSubmit}>
        <StaticModal.Header withCloseButton>{tWrapper('title')}</StaticModal.Header>
        <StaticModal.Content className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 gap-x-2">
            <span className="text-t14-sb text-text-secondary">{tWrapper('type')}</span>
            <span className="flex items-center gap-2 text-s18">
              <FontAwesomeIcon icon={faIndustryWindows} />
              {/* external name - should not be translated */}
              Production
            </span>
          </div>
          <div className="flex flex-col gap-4 gap-x-2">
            <span className="text-t14-sb text-text-secondary">{tWrapper('MESId')}</span>
            <span className="text-s18 uppercase">{externalName || '-'}</span>
          </div>
          <FormTextInput multiline required label={tWrapper('nameToDisplay')} maxLength={NAME_LENGTH} name="name" />
          <FormTextInput
            required
            label={tWrapper('shortName')}
            maxLength={SHORT_NAME_LENGTH}
            maxWidth={120}
            name="shortName"
          />
        </StaticModal.Content>
        <StaticModal.Footer>
          <Button variant="outlined" onClick={closeQueueDetailsModal}>
            {tWrapper('cancel')}
          </Button>
          <Button isLoading={queueMutation.isPending} type="submit" variant="contained">
            {tWrapper('confirmButton')}
          </Button>
        </StaticModal.Footer>
      </FormProvider>
    </StaticModal>
  );
};

export const { openQueueDetailsModal, closeQueueDetailsModal } = createModal(QueueDetailsModal, {
  name: 'QueueDetailsModal',
});
